import NiceModal from '@ebay/nice-modal-react';
import { ContactSupportOutlined } from '@mui/icons-material';
import { requester } from 'App';
import { devLog } from "utils/logger";
// 상수 정의
const BIOMETRIC_KEY_ALIAS = "puffu_biometric_key";
const PIN_STORAGE_KEY = "puffu_pin_status";
const PHONE_STORAGE_KEY = "puffu_user_phone";

// 버전 확인을 위한 콜백 추가
if (typeof window !== 'undefined') {
  window.showAppUpdateModal = function(latestVersion, storeType) {
    // 업데이트 모달 표시
    if (typeof NiceModal !== 'undefined') {
      NiceModal.show("updateModal", { 
        appVersion: latestVersion,
        storeType: storeType || 'one'  // 'one' 또는 'galaxy'
      });
    } else {
      console.error('NiceModal이 정의되지 않았습니다.');
    }
  };
}

// src/shared/BiometricService.js
class BiometricService {

  constructor() {
    // iOS 브릿지 초기화
    this._initializeIOSBridge();
  }

  /**
   * iOS 브릿지 초기화 (콜백 함수들을 미리 정의)
   * @private
   */
  _initializeIOSBridge() {
    if (typeof window !== 'undefined' && this.getPlatform() === 'ios') {
      devLog('[BiometricService] iOS 브릿지 초기화 시작');
      
      // 기본 브릿지 객체 생성
      window.BRIDGE = window.BRIDGE || {};
      window.iosBridge = window.iosBridge || {};
      
      // 🔥 기본 submitPin 핸들러 (타이밍 문제 방지 + 강력한 연동)
      window.iosBridge.submitPin = (pin) => {
        devLog("[BiometricService] 기본 submitPin 호출:", pin);
        
        // 여러 콜백 패턴 시도
        const tryCallback = (attempt = 1) => {
          devLog(`[BiometricService] 콜백 시도 ${attempt}/5`);
          
          // 1순위: PinVerificationManager 고정 콜백
          if (window.pinVerificationCallback) {
            devLog("[BiometricService] pinVerificationCallback 발견 - 연동");
            window.pinVerificationCallback(false, null, pin);
            return true;
          }
          
          // 2순위: 다른 가능한 콜백 패턴들 확인
          const possibleCallbacks = [
            'pinVerificationCallback',
            'FIXED_CALLBACK_NAME',
            'authCallback',
            'pinCallback'
          ];
          
          for (const callbackName of possibleCallbacks) {
            if (window[callbackName] && typeof window[callbackName] === 'function') {
              devLog(`[BiometricService] ${callbackName} 콜백 발견 - 연동`);
              window[callbackName](false, null, pin);
              return true;
            }
          }
          
          // 3순위: 전역 상태에 PIN 저장 (폴백)
          if (attempt === 1) {
            devLog("[BiometricService] 콜백 없음 - 전역 상태에 PIN 저장");
            window._pendingPinValue = pin;
            window._pendingPinTimestamp = Date.now();
          }
          
          // 재시도 (최대 5번, 총 2초)
          if (attempt < 5) {
            setTimeout(() => tryCallback(attempt + 1), 400);
          } else {
            console.error("[BiometricService] 모든 콜백 시도 실패 - PIN 값:", pin);
            devLog("[BiometricService] 사용 가능한 전역 객체:", Object.keys(window).filter(k => k.includes('pin') || k.includes('Pin') || k.includes('callback')));
          }
          
          return false;
        };
        
        tryCallback();
      };
      
      // 🔥 기본 loginResponse 핸들러 (타이밍 문제 방지)  
      window.iosBridge.loginResponse = (result) => {
        devLog("[BiometricService] 기본 loginResponse 호출:", result);
        console.warn("[BiometricService] loginResponse가 처리되지 않음 - PinVerificationManager 콜백 대기 중");
        // PinVerificationManager가 덮어쓸 때까지 대기
      };
      
      // 🔥 기타 필요한 콜백들만 미리 정의 (PIN 관련은 PinVerificationManager에서 관리)
      window.BRIDGE.authFaceIdCallback = (result) => {
        devLog("[BiometricService] 기본 authFaceIdCallback 호출:", result);
        console.warn("[BiometricService] authFaceIdCallback이 처리되지 않음");
      };
      
      window.BRIDGE.mainFaceIdCallback = (result) => {
        devLog("[BiometricService] 기본 mainFaceIdCallback 호출:", result);
        console.warn("[BiometricService] mainFaceIdCallback이 처리되지 않음");
      };
      
      // 🔥 phoneNumberCallback 미리 정의 (네이티브에서 자동 호출 대응)
      window.BRIDGE.phoneNumberCallback = (phoneNumber) => {
        devLog("[BiometricService] 네이티브에서 전화번호 자동 전달받음:", phoneNumber);
        
        if (phoneNumber) {
          // 로컬스토리지에 즉시 저장
          localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
          devLog("[BiometricService] 전화번호 로컬스토리지 저장 완료:", phoneNumber);
          
          // 전역 이벤트 발생 (AuthenticationFlow에서 감지 가능)
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('phoneNumberReceived', { 
              detail: { phoneNumber } 
            }));
          }
        }
      };
      
      // 🔥 phoneNumberSaveCallback 미리 정의
      window.BRIDGE.phoneNumberSaveCallback = (result) => {
        devLog("[BiometricService] 전화번호 저장 결과:", result);
        
        if (result && result.success) {
          devLog("[BiometricService] 네이티브 전화번호 저장 성공");
        } else {
          console.warn("[BiometricService] 네이티브 전화번호 저장 실패:", result);
        }
      };
      
      devLog('[BiometricService] iOS 브릿지 초기화 완료');
    }
  }

  /**
   * 생체인증 지원 여부 확인
   * @returns {boolean}
   */
  isSupported() {
    // 안드로이드 브릿지 확인
    const hasAndroidBridge = typeof window.BRIDGE !== 'undefined' &&
      (typeof window.BRIDGE.authenticateWithBiometric === 'function' ||
        typeof window.BRIDGE.authenticateAndGenerateKey === 'function');

    // iOS 브릿지 확인
    const hasIOSBridge = window.webkit?.messageHandlers?.BRIDGE !== undefined;

    return hasAndroidBridge || hasIOSBridge;
  }

  /**
   * 플랫폼 확인 (iOS/Android)
   * @returns {string} 'ios' 또는 'android'
   */
  getPlatform() {
    // iOS 브릿지 확인
    if (window.webkit?.messageHandlers?.BRIDGE) {
      return 'ios';
    }
    // 안드로이드 브릿지 확인
    else if (typeof window.BRIDGE !== 'undefined') {
      return 'android';
    }
    return 'unknown';
  }

  /**
   * 생체인증 키 등록
   * @param {string} userId - 사용자 ID
   * @returns {Promise<Object>} 등록 결과
   */
  registerBiometricKey(userId) {
    const platform = this.getPlatform();

    if (platform === 'android') {
      return this._registerAndroidKey(userId);
    } else if (platform === 'ios') {
      return this._registerIOSKey(userId);
    } else {
      return Promise.reject(new Error('지원하지 않는 플랫폼입니다.'));
    }
  }

  /**
   * 생체인증으로 데이터 서명 (인증)
   * @param {string} userId - 사용자 ID
   * @param {string} challenge - 서명할 챌린지 데이터
   * @param {string} mode - 'auth' 또는 'main' (iOS에서만 사용)
   * @returns {Promise<Object>} 서명 결과
   */
  authenticateAndSignData(userId, rawChallenge, mode = 'main') {
    const platform = this.getPlatform();

    // 사용자 ID와 타임스탬프를 포함한 증강된 챌린지 생성
    const enhancedChallenge = JSON.stringify({
      raw: rawChallenge,
      userId: userId,
      timestamp: Date.now()
    });

    if (platform === 'android') {
      return this._authenticateAndroid(userId, enhancedChallenge);
    } else if (platform === 'ios') {
      return this._authenticateIOS(enhancedChallenge, mode);
    } else {
      return Promise.reject(new Error('지원하지 않는 플랫폼입니다.'));
    }
  }

  /**
   * 안드로이드 키 등록
   * @private
   */
  _registerAndroidKey(userId) {
    return new Promise((resolve, reject) => {
      if (!window.BRIDGE?.authenticateAndGenerateKey) {
        reject(new Error('안드로이드 생체인증을 지원하지 않습니다.'));
        return;
      }

      const keyAlias = BIOMETRIC_KEY_ALIAS;
      const challenge = this._generateRandomChallenge();
      const callbackName = `biometricRegCallback_${Date.now()}`;

      // 콜백 등록
      window[callbackName] = (success, errorMessage, resultData) => {
        delete window[callbackName];

        if (success) {
          try {
            const result = JSON.parse(resultData);
            resolve({
              userId,
              publicKey: result.publicKey,
              algorithm: result.algorithm,
              keyAlias,
              challenge
            });
          } catch (error) {
            reject(new Error(`결과 파싱 오류: ${error.message}`));
          }
        } else {
          reject(new Error(errorMessage || '생체인증 키 등록 실패'));
        }
      };

      // 네이티브 브릿지 호출
      window.BRIDGE.authenticateAndGenerateKey(keyAlias, challenge, callbackName);
    });
  }

  /**
   * iOS 키 등록
   * @private
   */
  _registerIOSKey(userId) {
    return new Promise((resolve, reject) => {
      if (!window.webkit?.messageHandlers?.BRIDGE || !window.BRIDGE) {
        reject(new Error('iOS 생체인증을 지원하지 않습니다.'));
        return;
      }

      // 타임아웃 처리를 위한 변수
      let timeoutId = null;

      // 네이티브와 약속된 고정 콜백 이름 사용
      const callbackName = 'faceIdCallback';

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        // 콜백을 삭제하여 메모리 누수 방지 및 중복 호출 방지
        if (window.BRIDGE) {
          delete window.BRIDGE[callbackName];
        }
      };
      
      // 네이티브로부터 응답을 받을 콜백 함수 정의
      window.BRIDGE[callbackName] = (hexString) => {
        cleanup(); // 응답을 받으면 타임아웃과 콜백을 정리

        // 🔥 네이티브로부터 받은 문자열이 실제 성공인지, 아니면 에러 메시지인지 명확히 확인합니다.
        const isSuccess = hexString &&
                          hexString !== '' &&
                          !hexString.toUpperCase().includes('FAIL') && // 'FAILURE...'
                          !hexString.toUpperCase().includes('CANCEL'); // '...CANCELED'

        if (isSuccess) {
          devLog('[BiometricService] iOS hexString 수신 성공:', hexString);
          // 안드로이드와 유사한 구조로 반환 (publicKey 필드 사용)
          resolve({
            publicKey: hexString,
            algorithm: 'domainState' // 이 키의 종류를 명시
          });
        } else {
          devLog('[BiometricService] iOS 생체인증 등록 취소 또는 실패, 원본 메시지:', hexString);
          // '사용자 취소' 에러로 통일하여 상위 catch 블록에서 일관되게 처리하도록 합니다.
          reject(new Error('USER_CANCEL'));
        }
      };

      // 60초 타임아웃 설정
      timeoutId = setTimeout(() => {
        console.error(`[BiometricService] iOS 생체인증 등록 응답 시간 초과 (60초)`);
        cleanup();
        reject(new Error('BIOMETRIC_TIMEOUT'));
      }, 60000); 

      // iOS에 Face ID 등록 요청 전송
      window.webkit.messageHandlers.BRIDGE.postMessage("faceId");
    });
  }

  /**
   * 안드로이드 인증 및 서명
   * @private
   */
  _authenticateAndroid(userId, challenge) {
    return new Promise((resolve, reject) => {
      if (!window.BRIDGE?.authenticateAndSignData) {
        reject(new Error('안드로이드 생체인증을 지원하지 않습니다.'));
        return;
      }

      const keyAlias = "puffu_biometric_key";  // 고정 별칭
      const callbackName = `biometricAuthCallback_${Date.now()}`;

      window[callbackName] = (success, errorMessage, resultData) => {
        delete window[callbackName];

        if (success) {
          try {
            const result = JSON.parse(resultData);
            resolve({
              signature: result.signature,  // 안드로이드에서는 공개키가 반환됨
              data: result.data
            });
          } catch (error) {
            reject(new Error(`결과 파싱 오류: ${error.message}`));
          }
        } else {
          reject(new Error(errorMessage || '생체인증 실패'));
        }
      };

      window.BRIDGE.authenticateAndSignData(keyAlias, challenge, callbackName);
    });
  }

  /**
   * iOS 인증 및 서명
   * @private
   */
  _authenticateIOS(challenge, mode = 'main') { // challenge 파라미터는 사용되지 않음
    return new Promise((resolve, reject) => {
      if (!window.webkit?.messageHandlers?.BRIDGE || !window.BRIDGE) {
        reject(new Error('iOS 생체인증을 지원하지 않습니다.'));
        return;
      }

      // 타임아웃 처리를 위한 변수
      let timeoutId = null;
      // 네이티브와 약속된 고정 콜백 이름 사용
      const callbackName = mode === 'main' ? 'mainFaceIdCallback' : 'authFaceIdCallback';

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        // 콜백을 삭제하여 메모리 누수 방지 및 중복 호출 방지
        if (window.BRIDGE) {
          delete window.BRIDGE[callbackName];
        }
      };

      // 네이티브로부터 응답을 받을 콜백 함수 정의
      window.BRIDGE[callbackName] = (hexString) => {
        cleanup(); // 응답을 받으면 타임아웃과 콜백을 정리
        
        // 🔥 네이티브로부터 받은 문자열이 실제 성공인지, 아니면 에러 메시지인지 명확히 확인합니다.
        const isSuccess = hexString &&
                          hexString !== '' &&
                          !hexString.toUpperCase().includes('FAIL') &&
                          !hexString.toUpperCase().includes('CANCEL');

        if (isSuccess) {
          devLog(`[BiometricService] iOS (${mode}) hexString 인증 성공:`, hexString);
          // 안드로이드와 유사한 구조로 반환 (signature 필드 사용)
          resolve({
            signature: hexString
          });
        } else {
          devLog(`[BiometricService] iOS (${mode}) 생체인증 취소 또는 실패, 원본 메시지:`, hexString);
          // '사용자 취소' 에러로 통일하여 상위 catch 블록에서 일관되게 처리하도록 합니다.
          reject(new Error('USER_CANCEL'));
        }
      };

      // 60초 타임아웃 설정
      timeoutId = setTimeout(() => {
        console.error(`[BiometricService] iOS 생체인증 인증 응답 시간 초과 (60초)`);
        cleanup();
        reject(new Error('BIOMETRIC_TIMEOUT'));
      }, 60000);

      // iOS에 Face ID 인증 요청 전송
      const command = mode === 'main' ? 'mainFaceId' : 'authFaceId';
      window.webkit.messageHandlers.BRIDGE.postMessage({ command });
    });
  }

  /**
   * 랜덤 챌린지 생성
   * @private
   */
  _generateRandomChallenge(length = 32) {
    // 랜덤 바이트 배열 생성
    const randomBytes = new Uint8Array(length);
    window.crypto.getRandomValues(randomBytes);

    // Base64 인코딩
    const base64 = btoa(String.fromCharCode(...randomBytes));

    // URL 안전한 형태로 변환
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
 * 등록된 생체인증 키가 있는지 확인
 * @returns {Promise<boolean>} 키 존재 여부
 */
  hasRegisteredKey() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();

        if (platform === 'android') {
          // 안드로이드에서 키 존재 여부 확인
          if (window.BRIDGE?.checkBiometricKeyExists) {
            const callbackName = `biometricKeyCallback_${Date.now()}`;
            const keyAlias = "puffu_biometric_key";

            window[callbackName] = (exists) => {
              delete window[callbackName];
              resolve(exists === true || exists === 'true');
            };

            window.BRIDGE.checkBiometricKeyExists(keyAlias, callbackName);
          } else {
            // 네이티브 브릿지 메서드가 없으면 지원하지 않는 것으로 간주
            resolve(false);
          }
        } else if (platform === 'ios') {
          // iOS에서 키 존재 여부 확인
          if (window.webkit?.messageHandlers?.BRIDGE) {
            // 콜백 함수 설정
            window.BRIDGE = window.BRIDGE || {};
            window.BRIDGE.biometricKeyCallback = (exists) => {
              resolve(exists === true || exists === 'true');
            };

            // iOS에 요청 전송
            window.webkit.messageHandlers.BRIDGE.postMessage({
              command: "checkBiometricKeyExists"
            });
          } else {
            // 네이티브 브릿지가 없으면 지원하지 않는 것으로 간주
            resolve(false);
          }
        } else {
          // 알 수 없는 플랫폼이면 항상 false 반환
          resolve(false);
        }
      } catch (error) {
        console.error('생체인증 키 확인 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * PIN 등록 요청
   * @param {Function} callback - 등록 완료 후 호출될 콜백 함수
   * @returns {Promise<Object>} 등록 결과
   */
  requestPinRegistration(callback) {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();

        if (platform === 'android') {
          if (!window.BRIDGE?.showPinRegistration) {
            reject(new Error('PIN 등록을 지원하지 않습니다.'));
            return;
          }

          const callbackName = `pinRegistrationCallback_${Date.now()}`;

          // 콜백 등록
          window[callbackName] = (success, errorMessage, pinHash) => {
            delete window[callbackName];

            if (success) {
              // 등록 성공 시 로컬 상태 업데이트
              localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
                hasPin: true,
                registeredAt: Date.now()
              }));

              resolve({
                success: true,
                pinHash
              });

              if (typeof callback === 'function') {
                callback(true, pinHash);
              }
            } else {
              reject(new Error(errorMessage || 'PIN 등록 취소'));

              if (typeof callback === 'function') {
                callback(false, errorMessage);
              }
            }
          };

          // 네이티브 브릿지 호출
          window.BRIDGE.showPinRegistration(callbackName);
        } else if (platform === 'ios') {
          if (!window.webkit?.messageHandlers?.BRIDGE) {
            reject(new Error('iOS PIN 등록을 지원하지 않습니다.'));
            return;
          }

          // 전역 콜백 함수 설정
          window.BRIDGE = window.BRIDGE || {};
          window.BRIDGE.pinRegistrationCallback = (result) => {
            if (result && result.success) {
              // 등록 성공 시 로컬 상태 업데이트
              localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
                hasPin: true,
                registeredAt: Date.now()
              }));

              resolve({
                success: true,
                pinHash: result.pinHash
              });

              if (typeof callback === 'function') {
                callback(true, result.pinHash);
              }
            } else {
              const errorMsg = result?.errorMessage || 'PIN 등록 취소';
              reject(new Error(errorMsg));

              if (typeof callback === 'function') {
                callback(false, errorMsg);
              }
            }
          };

          // iOS에 PIN 등록 요청 전송
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "pinCreate"
          });
        } else {
          reject(new Error('지원하지 않는 플랫폼입니다.'));
        }
      } catch (error) {
        console.error('PIN 등록 요청 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * PIN 확인 (인증) 요청
   * @param {Function} callback - 확인 완료 후 호출될 콜백 함수 (success, errorMessage, pinValue)
   * @param {string} mode - 호출 모드 ('auth' 또는 'main')
   * @returns {Promise<Object>} 확인 결과
   */
  verifyPin(callback, mode = 'main') {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();

        if (platform === 'android') {
          if (!window.BRIDGE?.verifyPin) {
            reject(new Error('PIN 확인을 지원하지 않습니다.'));
            return;
          }

          // 🔥 고정 콜백 이름 사용 (PinVerificationManager와 일치)
          const callbackName = 'pinVerificationCallback';
          devLog(`[BiometricService] 고정 PIN 검증 콜백 사용: ${callbackName}`);

          // 기존 콜백 함수 삭제 (중복 방지)
          if (window[callbackName]) {
            delete window[callbackName];
            devLog(`[BiometricService] 기존 콜백 함수 삭제: ${callbackName}`);
          }

          // 콜백 등록 - PIN 값도 함께 전달받도록 수정
          window[callbackName] = (success, errorMessage, pinValue) => {
            devLog(`[BiometricService] PIN 검증 콜백 호출: 성공=${success}, PIN값=${!!pinValue}, 콜백=${callbackName}`);
            
            // Promise는 항상 resolve로 처리하고, 실제 결과는 callback으로 전달 (수정)
            resolve({ success: success, pinValue: pinValue, errorMessage: errorMessage });

            // 콜백 함수 호출
            if (typeof callback === 'function') {
              callback(success, errorMessage, pinValue);
            }
          };

          // 네이티브 브릿지 호출
          devLog(`[BiometricService] 네이티브 PIN 검증 호출: ${callbackName}`);
          window.BRIDGE.verifyPin(callbackName);
        } else if (platform === 'ios') {
          if (!window.webkit?.messageHandlers?.BRIDGE) {
            reject(new Error('iOS PIN 확인을 지원하지 않습니다.'));
            return;
          }

          // 🔥 iOS 브릿지 초기화 (스위프트 개발자 명세 준수)
          window.iosBridge = window.iosBridge || {};
          
          devLog(`[BiometricService] iOS PIN 검증 시작 - 모드: ${mode}`);
          
          // 🔥 스위프트 명세: PIN 번호 입력 콜백
          window.iosBridge.loginResponse = (result) => {
            devLog("[BiometricService] iOS loginResponse 콜백:", result);
            // AuthenticationFlow(로그인)에서만 이 콜백 사용
            if (mode === 'auth') {
              const success = result === "success";
              const errorMsg = success ? null : 'PIN 확인 실패';
              
              // Promise resolve
              resolve({ success: success, pinValue: null, errorMessage: errorMsg });
              
              // 콜백 함수 호출
              if (typeof callback === 'function') {
                callback(success, errorMsg, null);
              }
            }
          };
          
          // 🔥 스위프트 명세: PIN 번호 검증 콜백
          window.iosBridge.submitPin = (pin) => {
            devLog("[BiometricService] iOS submitPin 콜백:", pin);
            
            // Main2(결제)에서만 이 콜백 사용
            if (mode === 'main') {
              const success = pin && pin.length > 0;
              const errorMsg = success ? null : 'PIN 입력 취소';
              
              // Promise resolve
              resolve({
                success: success,
                pinValue: pin,
                errorMessage: errorMsg
              });
              
              // 콜백 함수 호출
              if (typeof callback === 'function') {
                callback(success, errorMsg, pin);
              }
            }
          };
          
          // 🔥 스위프트 명세: pinCheck 명령어 사용
          window.webkit.messageHandlers.BRIDGE.postMessage("pinCheck");
        } else {
          reject(new Error('지원하지 않는 플랫폼입니다.'));
        }
      } catch (error) {
        console.error('PIN 확인 요청 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * PIN 등록 여부 확인
   * @returns {boolean} PIN 등록 여부
   */
  hasPin() {
    const platform = this.getPlatform();
    devLog(`[BiometricService] hasPin() 호출 - 플랫폼: ${platform}`);

    if (platform === 'android') {
      try {
        if (window.BRIDGE && typeof window.BRIDGE.hasPin === 'function') {
          const result = window.BRIDGE.hasPin();
          devLog(`[BiometricService] 안드로이드 hasPin 결과: ${result}`);
          return result;
        } else {
          console.warn("[BiometricService] 안드로이드 BRIDGE.hasPin 함수 없음");
          return false;
        }
      } catch (error) {
        console.error("[BiometricService] 안드로이드 hasPin 오류:", error);
        return false;
      }
    } else if (platform === 'ios') {
      return new Promise((resolve) => {
        try {
          devLog("[BiometricService] iOS hasPin 확인 시작");
          
          // 🔥 네이티브 명령어 실패에 대한 타임아웃 처리
          const timeoutId = setTimeout(() => {
            console.warn("[BiometricService] iOS hasPin 타임아웃 - 네이티브 명령어 미지원으로 간주");
            resolve(false); // 타임아웃 시 false 반환
          }, 1000); // 1초 타임아웃

          // 콜백 함수 등록
          window.BRIDGE.biometricKeyExistsCallback = (exists) => {
            clearTimeout(timeoutId);
            devLog(`[BiometricService] iOS hasPin 콜백 결과: ${exists}`);
            resolve(exists);
            delete window.BRIDGE.biometricKeyExistsCallback;
          };

          // 네이티브에 요청
          if (window.webkit?.messageHandlers?.BRIDGE) {
            window.webkit.messageHandlers.BRIDGE.postMessage({
              command: "checkBiometricKeyExists"
            });
            devLog("[BiometricService] iOS checkBiometricKeyExists 명령어 전송");
          } else {
            clearTimeout(timeoutId);
            console.warn("[BiometricService] iOS 웹킷 브릿지 없음");
            resolve(false);
          }
        } catch (error) {
          console.error("[BiometricService] iOS hasPin 오류:", error);
          resolve(false);
        }
      });
    } else {
      // 🔥 웹 환경이거나 알 수 없는 플랫폼인 경우 로컬스토리지 확인
      try {
        const hasStoredPin = localStorage.getItem('puffu_pin_exists') === 'true';
        devLog(`[BiometricService] 로컬스토리지 PIN 확인 결과: ${hasStoredPin}`);
        return hasStoredPin;
      } catch (error) {
        console.error("[BiometricService] 로컬스토리지 PIN 확인 오류:", error);
        return false;
      }
    }
  }

  /**
 * PIN 실패 횟수 초기화 (Android에서만 지원)
 * @returns {Promise<boolean>} 성공 여부
 */
  resetPinFailCount() {
    return new Promise((resolve, reject) => {
      const platform = this.getPlatform();

      if (platform === 'android') {
        if (!window.BRIDGE?.resetPinFailCount) {
          reject(new Error('PIN 실패 초기화를 지원하지 않습니다.'));
          return;
        }

        try {
          window.BRIDGE.resetPinFailCount();
          resolve(true);
        } catch (error) {
          reject(new Error('PIN 실패 초기화 중 오류 발생: ' + error.message));
        }
      } else {
        // iOS는 SharedPreferences가 없으므로 필요에 따라 noop 처리
        resolve(false);
      }
    });
  }

  /**
   * PIN 변경 요청
   * @param {Function} callback - 변경 완료 후 호출될 콜백 함수
   * @returns {Promise<Object>} 변경 결과
   */
  changePin(callback) {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();

        if (platform === 'android') {
          if (!window.BRIDGE?.changePin) {
            reject(new Error('PIN 변경을 지원하지 않습니다.'));
            return;
          }

          const callbackName = `pinChangeCallback_${Date.now()}`;

          // 콜백 등록
          window[callbackName] = (success, errorMessage, newPinHash) => {
            delete window[callbackName];

            if (success) {
              // 변경 성공 시 로컬 상태 업데이트
              localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
                hasPin: true,
                registeredAt: Date.now()
              }));

              resolve({
                success: true,
                pinHash: newPinHash
              });

              if (typeof callback === 'function') {
                callback(true, newPinHash);
              }
            } else {
              reject(new Error(errorMessage || 'PIN 변경 취소'));

              if (typeof callback === 'function') {
                callback(false, errorMessage);
              }
            }
          };

          // 네이티브 브릿지 호출
          window.BRIDGE.changePin(callbackName);
        } else if (platform === 'ios') {
          if (!window.webkit?.messageHandlers?.BRIDGE) {
            reject(new Error('iOS PIN 변경을 지원하지 않습니다.'));
            return;
          }

          // 전역 콜백 함수 설정
          window.BRIDGE = window.BRIDGE || {};
          window.BRIDGE.pinChangeCallback = (result) => {
            if (result && result.success) {
              // 변경 성공 시 로컬 상태 업데이트
              localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
                hasPin: true,
                registeredAt: Date.now()
              }));

              resolve({
                success: true,
                pinHash: result.pinHash
              });

              if (typeof callback === 'function') {
                callback(true, result.pinHash);
              }
            } else {
              const errorMsg = result?.errorMessage || 'PIN 변경 취소';
              reject(new Error(errorMsg));

              if (typeof callback === 'function') {
                callback(false, errorMsg);
              }
            }
          };

          // iOS에 PIN 변경 요청 전송
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "changePin"
          });
        } else {
          reject(new Error('지원하지 않는 플랫폼입니다.'));
        }
      } catch (error) {
        console.error('PIN 변경 요청 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * 휴대폰 번호 저장
   * @param {string} phoneNumber - 저장할 휴대폰 번호
   */
  savePhoneNumber(phoneNumber) {
    try {
      const platform = this.getPlatform();
      
      if (platform === 'android') {
        // 안드로이드는 기존 동기 방식 유지
        if (window.BRIDGE?.savePhoneNumber) {
          window.BRIDGE.savePhoneNumber(phoneNumber);
          return true;
        }
      } else if (platform === 'ios') {
        // iOS는 비동기 방식으로 처리
        if (window.webkit?.messageHandlers?.BRIDGE) {
          // 콜백 등록
          window.BRIDGE = window.BRIDGE || {};
          window.BRIDGE.phoneNumberSaveCallback = (result) => {
            devLog('[BiometricService] iOS 전화번호 저장 결과:', result);
            if (result.success) {
              // 성공 시 로컬스토리지에도 백업 저장
              localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
            }
          };
          
          // iOS에 저장 요청 (네이티브에서 savePhoneNumber와 setPhoneNumber 둘다 처리 필요)
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "savePhoneNumber",
            phoneNumber: phoneNumber
          });
          
          // iOS는 비동기이므로 로컬스토리지에도 즉시 저장 (백업용)
          localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
          return true;
        }
      }
      
      // 네이티브 구현이 없는 경우 로컬 스토리지 폴백
      localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
      return true;
    } catch (error) {
      console.error('휴대폰 번호 저장 오류:', error);
      return false;
    }
  }

  /**
   * 저장된 휴대폰 번호 가져오기
   * @returns {string|null} 저장된 휴대폰 번호 또는 null
   */
  getPhoneNumber() {
    try {
      const platform = this.getPlatform();
      
      if (platform === 'android') {
        // 안드로이드 네이티브 호출
        if (window.BRIDGE?.getPhoneNumber) {
          return window.BRIDGE.getPhoneNumber();
        }
      } else if (platform === 'ios') {
        // iOS는 이미 구현되어 있지만, 동기 조회를 위해 로컬스토리지 우선 사용
        const localNumber = localStorage.getItem(PHONE_STORAGE_KEY);
        
        // 백그라운드에서 iOS 네이티브도 호출 (최신 상태 동기화용)
        if (window.webkit?.messageHandlers?.BRIDGE) {
          // 기존 phoneNumberCallback은 이미 구현되어 있음 (line ~520)
          window.webkit.messageHandlers.BRIDGE.postMessage("getPhoneNumber");
        }
        
        return localNumber;
      }
      
      // 네이티브 구현이 없는 경우 로컬 스토리지 폴백
      return localStorage.getItem(PHONE_STORAGE_KEY);
    } catch (error) {
      console.error('휴대폰 번호 조회 오류:', error);
      return null;
    }
  }

  /**
   * 로그인 상태 리셋
   * @param {Function} dispatch - Redux dispatch 함수
   */
  resetLoginState(dispatch) {
    if (dispatch && dispatch.constructor === Function) {
      // AuthReducer.actions.reset() 호출
      try {
        const { AuthReducer } = require('./redux/reducers/auth/AuthReducer');
        dispatch(AuthReducer.actions.reset());
      } catch (error) {
        console.error('Auth reducer reset 호출 오류:', error);
      }
    }
    
    // 로컬 스토리지 정리
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      // 기타 필요한 정리 작업...
    } catch (error) {
      console.error('로컬 스토리지 정리 오류:', error);
    }
  }

  /**
   * PIN 직접 저장 (UI 없이)
   * @param {string} userId - 사용자 ID
   * @param {string} pinNumber - 저장할 PIN 번호
   * @returns {Promise<Object>} 저장 결과
   */
  savePinDirectly(userId, pinNumber) {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform === 'android') {
          if (!window.BRIDGE?.savePinDirectly) {
            // 네이티브 함수가 없으면 localStorage에만 PIN 상태 저장
            localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
              hasPin: true,
              registeredAt: Date.now()
            }));
            
            // 네이티브 함수가 없으면 성공으로 간주하고 해시된 PIN 반환
            resolve({
              success: true,
              pinHash: { hashedPin: "placeholder_hash_" + Date.now() }
            });
            return;
          }
          
          const callbackName = `pinDirectSaveCallback_${Date.now()}`;
          
          // 콜백 등록
          window[callbackName] = (success, errorMessage, pinHash) => {
            delete window[callbackName];
            
            if (success) {
              // 저장 성공 시 로컬 상태 업데이트
              localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
                hasPin: true,
                registeredAt: Date.now()
              }));
              
              resolve({
                success: true,
                pinHash
              });
            } else {
              reject(new Error(errorMessage || 'PIN 저장 실패'));
            }
          };
          
          // 네이티브 브릿지 호출
          window.BRIDGE.savePinDirectly(userId, pinNumber, callbackName);
        } else if (platform === 'ios') {
          if (!window.webkit?.messageHandlers?.BRIDGE) {
            // 네이티브 함수가 없으면 localStorage에만 PIN 상태 저장
            localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
              hasPin: true,
              registeredAt: Date.now()
            }));
            
            // 네이티브 함수가 없으면 성공으로 간주하고 해시된 PIN 반환
            resolve({
              success: true,
              pinHash: { hashedPin: "placeholder_hash_" + Date.now() }
            });
            return;
          }
          
          // 전역 콜백 함수 설정
          window.BRIDGE = window.BRIDGE || {};
          window.BRIDGE.receivePIN = (result) => {
            devLog("[BiometricService] receivePIN", result);
          };
          window.BRIDGE.pinDirectSaveCallback = (result) => {
            if (result && result.success) {
              // 저장 성공 시 로컬 상태 업데이트
              localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
                hasPin: true,
                registeredAt: Date.now()
              }));
              
              resolve({
                success: true,
                pinHash: result.pinHash
              });
            } else {
              const errorMsg = result?.errorMessage || 'PIN 저장 실패';
              reject(new Error(errorMsg));
            }
          };
          
          // iOS에 PIN 저장 요청 전송
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "savePinDirectly",
            userId: userId,
            pinNumber: pinNumber
          });
        } else {
          // 웹에서는 로컬 스토리지에만 상태 저장
          localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
            hasPin: true,
            registeredAt: Date.now()
          }));
          
          // 실제 PIN은 저장하지 않고 성공으로 간주
          resolve({
            success: true,
            pinHash: { hashedPin: "placeholder_hash_" + Date.now() }
          });
        }
      } catch (error) {
        console.error('PIN 직접 저장 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * 서버 PIN 검증 성공을 네이티브에 알림
   * @param {string} pinNumber - 검증된 PIN 번호
   * @param {string} callbackName - 결과를 받을 콜백 함수 이름 (선택적)
   * @returns {boolean} 알림 전송 성공 여부
   */
  notifyPinVerificationSuccess(pinNumber, callbackName = null) {
    try {
      devLog("[BiometricService] 서버 PIN 검증 성공을 네이티브에 알림");
      const platform = this.getPlatform();
      
      if (platform === 'android') {
        // 1순위: 전용 성공 함수 사용
        if (window.BRIDGE?.notifyPinVerificationSuccess) {
          if (callbackName) {
            window.BRIDGE.notifyPinVerificationSuccess(pinNumber, callbackName);
            devLog("[BiometricService] 안드로이드에 PIN 검증 성공 알림 전송 완료 (콜백 포함)");
          } else {
            window.BRIDGE.notifyPinVerificationSuccess(pinNumber);
            devLog("[BiometricService] 안드로이드에 PIN 검증 성공 알림 전송 완료");
          }
          return true;
        }
        // 2순위: 기존 서버 검증 성공 함수 재사용 (프래그먼트 종료용)
        else if (window.BRIDGE?.notifyServerVerificationSuccess) {
          devLog("[BiometricService] 전용 함수 없음 - 기존 notifyServerVerificationSuccess 사용");
          window.BRIDGE.notifyServerVerificationSuccess();
          devLog("[BiometricService] 기존 함수로 프래그먼트 종료 신호 전송 완료");
          return true;
        }
        // 3순위: Android 브릿지 폴백
        else if (window.Android?.notifyServerVerificationSuccess) {
          devLog("[BiometricService] Android 브릿지로 프래그먼트 종료 신호 전송");
          window.Android.notifyServerVerificationSuccess();
          return true;
        }
        else {
          console.warn("[BiometricService] 안드로이드 PIN 성공 처리 함수 없음 - 모든 폴백 실패");
        }
      } else if (platform === 'ios') {
        if (window.webkit?.messageHandlers?.BRIDGE) {
          const message = {
            command: "notifyPinVerificationSuccess",
            pinNumber: pinNumber
          };
          
          if (callbackName) {
            message.callbackName = callbackName;
          }
          
          window.webkit.messageHandlers.BRIDGE.postMessage(message);
          devLog("[BiometricService] iOS에 PIN 검증 성공 알림 전송 완료");
          return true;
        } else {
          console.warn("[BiometricService] iOS 브릿지 없음");
        }
      } else {
        console.warn("[BiometricService] 지원되지 않는 플랫폼:", platform);
      }
    } catch (error) {
      console.error("[BiometricService] PIN 검증 성공 알림 오류:", error);
    }
    
    return false;
  }

  /**
   * 서버 PIN 검증 실패를 네이티브에 알림 (프래그먼트 재시작용)
   * @returns {boolean} 알림 전송 성공 여부
   */
  notifyPinVerificationFailedAndRestart() {
    try {
      devLog("[BiometricService] 서버 PIN 검증 실패를 네이티브에 알림 (재시작 요청)");
      const platform = this.getPlatform();
      
      if (platform === 'android') {
        if (window.BRIDGE?.notifyPinVerificationFailedAndRestart) {
          window.BRIDGE.notifyPinVerificationFailedAndRestart();
          devLog("[BiometricService] 안드로이드에 PIN 검증 실패 및 재시작 알림 전송 완료");
          return true;
        } else {
          console.warn("[BiometricService] 안드로이드 notifyPinVerificationFailedAndRestart 함수 없음");
          
          // 폴백: 직접 재시작 핸들러 호출
          if (window.restartPinVerification) {
            devLog("[BiometricService] 폴백: 직접 재시작 핸들러 호출");
            setTimeout(() => {
              window.restartPinVerification();
            }, 500);
            return true;
          }
        }
      } else if (platform === 'ios') {
        if (window.webkit?.messageHandlers?.BRIDGE) {
          window.webkit.messageHandlers.BRIDGE.postMessage({
            command: "notifyPinVerificationFailedAndRestart"
          });
          devLog("[BiometricService] iOS에 PIN 검증 실패 및 재시작 알림 전송 완료");
          return true;
        } else {
          console.warn("[BiometricService] iOS 브릿지 없음");
        }
      } else {
        console.warn("[BiometricService] 지원되지 않는 플랫폼:", platform);
      }
    } catch (error) {
      console.error("[BiometricService] PIN 검증 실패 및 재시작 알림 오류:", error);
    }
    
    return false;
  }

  /**
   * 네이티브에 저장된 모든 인증 관련 데이터 완전 초기화
   * @returns {Promise<boolean>} 초기화 성공 여부
   */
  clearAllAuthenticationData() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform === 'android') {
          if (!window.BRIDGE?.clearAllAuthenticationData) {
            reject(new Error('안드로이드 데이터 초기화를 지원하지 않습니다.'));
            return;
          }
          
          const callbackName = `clearAuthDataCallback_${Date.now()}`;
          
          // 콜백 등록
          window[callbackName] = (success, errorMessage) => {
            delete window[callbackName];
            
            if (success) {
              // 웹 로컬 스토리지도 함께 정리
              this._clearWebStorageData();
              resolve(true);
            } else {
              reject(new Error(errorMessage || '네이티브 데이터 초기화 실패'));
            }
          };
          
          // 네이티브 브릿지 호출
          window.BRIDGE.clearAllAuthenticationData(callbackName);
        } else if (platform === 'ios') {
          if (!window.webkit?.messageHandlers?.BRIDGE) {
            reject(new Error('iOS 데이터 초기화를 지원하지 않습니다.'));
            return;
          }
          
          // 전역 콜백 함수 설정
          window.BRIDGE = window.BRIDGE || {};
          window.BRIDGE.clearAuthDataCallback = (result) => {
            if (result && result.success) {
              // 웹 로컬 스토리지도 함께 정리
              this._clearWebStorageData();
              resolve(true);
            } else {
              reject(new Error(result?.errorMessage || '데이터 삭제 실패'));
            }
          };
          
          // iOS에 데이터 초기화 요청 전송
          // window.webkit.messageHandlers.BRIDGE.postMessage({
          //   command: "clearAllAuthenticationData"
          // });
          window.webkit.messageHandlers.BRIDGE.postMessage("clearAllAuthenticationData");
        } else {
          // 웹 환경에서는 로컬 스토리지만 정리
          this._clearWebStorageData();
          resolve(true);
        }
      } catch (error) {
        console.error('인증 데이터 초기화 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * 웹 로컬 스토리지 인증 관련 데이터 정리
   * @private
   */
  _clearWebStorageData() {
    try {
      // 생체인증 관련 데이터 삭제
      localStorage.removeItem('puffu_biometric_supported');
      
      // PIN 관련 데이터 삭제
      localStorage.removeItem(PIN_STORAGE_KEY);
      
      // 🔥 휴대폰 번호는 유지 (다음 로그인 시 편의를 위해)
      // localStorage.removeItem(PHONE_STORAGE_KEY); // 삭제 안 함
      
      // 기타 인증 관련 데이터 삭제
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('puffu_user_phone');
      
      devLog('[BiometricService] 웹 로컬 스토리지 인증 데이터 정리 완료 (휴대폰 번호 유지)');
    } catch (error) {
      console.error('[BiometricService] 웹 로컬 스토리지 정리 오류:', error);
    }
  }

  /**
   * PIN 등록 요청 (iOS 명세)
   * @returns {Promise<Object>} 등록 결과
   */
  requestPinCreationIOS() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS PIN 등록을 지원하지 않습니다.'));
          return;
        }

        // iOS 브릿지 초기화
        window.BRIDGE = window.BRIDGE || {};
        
        // PIN 등록 완료 콜백 설정
        window.BRIDGE.receivePIN = (digest) => {
          devLog("[BiometricService] PIN 등록 완료, digest:", digest);
          
          // 로컬 상태 업데이트
          localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
            hasPin: true,
            registeredAt: Date.now()
          }));
          
          resolve({
            success: true,
            digest: digest
          });
        };
        
        // iOS에 PIN 등록 요청
        window.webkit.messageHandlers.BRIDGE.postMessage("pinCreate");
        
      } catch (error) {
        console.error('[BiometricService] PIN 등록 요청 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * PIN 상태 확인 (iOS 명세)
   * @returns {Promise<boolean>} PIN 저장 여부
   */
  checkPinStatusIOS() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS PIN 상태 확인을 지원하지 않습니다.'));
          return;
        }

        // iOS 브릿지 초기화
        window.BRIDGE = window.BRIDGE || {};
        
        // PIN 상태 확인 콜백 설정
        window.BRIDGE.isPINSavedCallback = (result) => {
          devLog("[BiometricService] PIN 상태 확인 결과:", result);
          
          // 로컬 상태도 업데이트
          localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify({
            hasPin: result,
            checkedAt: Date.now()
          }));
          
          resolve(result === true || result === 'true');
        };
        
        // iOS에 PIN 상태 확인 요청
        window.webkit.messageHandlers.BRIDGE.postMessage("isPINSaved");
        
      } catch (error) {
        console.error('[BiometricService] PIN 상태 확인 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * PIN 검증 요청 (iOS 명세 - 모드별)
   * @param {string} mode - 'auth' 또는 'main'
   * @returns {Promise<Object>} 검증 결과
   */
  verifyPinIOS(mode = 'main') {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS PIN 검증을 지원하지 않습니다.'));
          return;
        }

        // iOS 브릿지 초기화
        window.iosBridge = window.iosBridge || {};
        
        devLog(`[BiometricService] iOS PIN 검증 시작 - 모드: ${mode}`);
        
        if (mode === 'auth') {
          // AuthenticationFlow용 콜백
          window.iosBridge.loginResponse = (result) => {
            devLog("[BiometricService] iOS loginResponse 콜백:", result);
            
            const success = result === "success";
            resolve({
              success: success,
              mode: 'auth',
              result: result
            });
          };
        } else {
          // Main2용 콜백
          window.iosBridge.submitPin = (pin) => {
            devLog("[BiometricService] iOS submitPin 콜백:", pin);
            
            const success = pin && pin.length > 0;
            resolve({
              success: success,
              mode: 'main',
              pin: pin
            });
          };
        }
        
        // iOS에 PIN 검증 요청 (type 파라미터 포함)
        window.webkit.messageHandlers.BRIDGE.postMessage({
          command: "pinCheck",
          type: mode
        });
        
      } catch (error) {
        console.error('[BiometricService] PIN 검증 요청 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * PIN 실패 횟수 초기화 (iOS 명세)
   * @returns {Promise<boolean>} 성공 여부
   */
  resetPinFailCountIOS() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS PIN 실패 횟수 초기화를 지원하지 않습니다.'));
          return;
        }

        // iOS에 실패 횟수 초기화 요청
        window.webkit.messageHandlers.BRIDGE.postMessage("resetCheckPoint");
        
        devLog("[BiometricService] PIN 실패 횟수 초기화 요청 완료");
        resolve(true);
        
      } catch (error) {
        console.error('[BiometricService] PIN 실패 횟수 초기화 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * 휴대폰 번호 저장 (iOS 명세)
   * @param {string} phoneNumber - 저장할 휴대폰 번호
   * @returns {Promise<boolean>} 성공 여부
   */
  savePhoneNumberIOS(phoneNumber) {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS 휴대폰 번호 저장을 지원하지 않습니다.'));
          return;
        }

        // iOS에 휴대폰 번호 저장 요청
        window.webkit.messageHandlers.BRIDGE.postMessage({
          command: "savePhoneNumber",
          phoneNumber: phoneNumber
        });
        
        devLog("[BiometricService] 휴대폰 번호 저장 요청 완료:", phoneNumber);
        
        // 로컬 스토리지에도 저장
        localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
        
        resolve(true);
        
      } catch (error) {
        console.error('[BiometricService] 휴대폰 번호 저장 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * 저장된 휴대폰 번호 가져오기 (iOS 명세)
   * @returns {Promise<string>} 저장된 휴대폰 번호
   */
  getPhoneNumberIOS() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS 휴대폰 번호 조회를 지원하지 않습니다.'));
          return;
        }

        // iOS 브릿지 초기화
        window.BRIDGE = window.BRIDGE || {};
        
        // 휴대폰 번호 조회 콜백 설정
        window.BRIDGE.phoneNumberCallback = (phoneNumber) => {
          devLog("[BiometricService] 휴대폰 번호 조회 결과:", phoneNumber);
          
          // 로컬 스토리지에도 저장
          if (phoneNumber) {
            localStorage.setItem(PHONE_STORAGE_KEY, phoneNumber);
          }
          
          resolve(phoneNumber || '');
        };
        
        // iOS에 휴대폰 번호 조회 요청
        window.webkit.messageHandlers.BRIDGE.postMessage("getPhoneNumber");
        
      } catch (error) {
        console.error('[BiometricService] 휴대폰 번호 조회 오류:', error);
        reject(error);
      }
    });
  }

  /**
   * 모든 인증 데이터 삭제 (iOS 명세)
   * @returns {Promise<boolean>} 성공 여부
   */
  clearAllAuthenticationDataIOS() {
    return new Promise((resolve, reject) => {
      try {
        const platform = this.getPlatform();
        
        if (platform !== 'ios') {
          reject(new Error('iOS 전용 기능입니다.'));
          return;
        }

        if (!window.webkit?.messageHandlers?.BRIDGE) {
          reject(new Error('iOS 데이터 삭제를 지원하지 않습니다.'));
          return;
        }

        // iOS 브릿지 초기화
        window.BRIDGE = window.BRIDGE || {};
        
        // 데이터 삭제 완료 콜백 설정
        window.BRIDGE.clearAuthDataCallback = (result) => {
          devLog("[BiometricService] 인증 데이터 삭제 결과:", result);
          
          if (result && (result.success === true || result === true)) {
            // 웹 로컬 스토리지도 함께 정리
            this._clearWebStorageData();
            resolve(true);
          } else {
            reject(new Error(result?.errorMessage || '데이터 삭제 실패'));
          }
        };
        
        // iOS에 데이터 초기화 요청 전송
        // window.webkit.messageHandlers.BRIDGE.postMessage({
        //   command: "clearAllAuthenticationData"
        // });
        window.webkit.messageHandlers.BRIDGE.postMessage("clearAllAuthenticationData");
      } catch (error) {
        console.error('[BiometricService] 인증 데이터 삭제 오류:', error);
        reject(error);
      }
    });
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const biometricService = new BiometricService();
export default biometricService;